import { Injectable } from '@angular/core';
import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { Observable, combineLatest, from, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { AuthTokenService } from '../services/auth-token.service';
import { KeycloakClientService } from '../services/keycloak-client.service';

@Injectable()
export class AuthBearerInterceptor implements HttpInterceptor {
    constructor(
        private keycloakClient: KeycloakClientService,
        private authToken: AuthTokenService
    ) {}

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (!this.keycloakClient.bearerInterceptorEnabled) {
            return next.handle(req);
        }

        if (!this.keycloakClient.shouldAddToken(req)) {
            return next.handle(req);
        }

        return combineLatest([
            from(this.conditionallyUpdateToken(req)),
            of(this.keycloakClient.isLoggedIn()),
        ]).pipe(
            mergeMap(([, isLoggedIn]) =>
                isLoggedIn ? this.handleRequestWithTokenHeader(req, next) : next.handle(req)
            )
        );
    }

    private async conditionallyUpdateToken(req: HttpRequest<unknown>): Promise<boolean> {
        if (this.keycloakClient.shouldUpdateToken(req)) {
            return this.authToken.updateToken();
        }

        return true;
    }

    private handleRequestWithTokenHeader(
        req: HttpRequest<unknown>,
        next: HttpHandler
    ): Observable<HttpEvent<unknown>> {
        return this.keycloakClient.addTokenToHeader(req.headers).pipe(
            mergeMap(headersWithBearer => {
                const authorizedReq = req.clone({ headers: headersWithBearer });
                return next.handle(authorizedReq);
            })
        );
    }
}
